import { useEffect, useState } from 'react';
import Reveal from '../../components/Reveal';
import SafeImage from '../../components/SafeImage';
import './packages.css';
import {
  DESTINATION_IMAGES,
  SAFARI_PAGE_IMAGES,
  ZANZIBAR_PAGE_IMAGES,
} from '../../data/destinationImages';
import { getHeroSlidesToLoad, preloadImage } from '../../utils/imageLoading';

const PACKAGES_HERO_SLIDES = [
  {
    id: 'beach-sunset',
    src: 'https://images.pexels.com/photos/34011241/pexels-photo-34011241.jpeg?auto=compress&cs=tinysrgb&w=1280',
  },
  {
    id: 'mountain-trail',
    src: 'https://images.pexels.com/photos/15993988/pexels-photo-15993988.jpeg?auto=compress&cs=tinysrgb&w=1280',
  },
  {
    id: 'wildlife-tree',
    src: 'https://images.pexels.com/photos/1931293/pexels-photo-1931293.jpeg?auto=compress&cs=tinysrgb&w=1280',
  },
  {
    id: 'manyara-safari',
    src: 'https://images.pexels.com/photos/18117636/pexels-photo-18117636.jpeg?auto=compress&cs=tinysrgb&w=1280',
  },
];

const HERO_SLIDE_INTERVAL_MS = 5500;

const PACKAGE_STATS = [
  { value: '3-14', label: 'Day options' },
  { value: '100%', label: 'Tailor-made' },
  { value: '24/7', label: 'Local support' },
  { value: '4', label: 'Trip styles' },
];

const FEATURED_PACKAGES = [
  {
    image: SAFARI_PAGE_IMAGES.northern,
    title: 'Classic Northern Circuit Safari',
    duration: '6-8 days',
    tag: 'Safari classic',
    route: 'Arusha, Tarangire, Ngorongoro, Serengeti',
    bestFor: 'First-time safari travellers and wildlife photographers',
    text: 'The signature Tanzania safari, combining big cats, elephant country, crater wildlife, and the open plains of the Serengeti.',
    highlights: ['Private 4x4 game drives', 'Ngorongoro crater descent', 'Serengeti migration window options'],
  },
  {
    image: DESTINATION_IMAGES.kilimanjaro,
    title: 'Kilimanjaro & Safari Combo',
    duration: '10-12 days',
    tag: 'Adventure',
    route: 'Kilimanjaro, Arusha, Ngorongoro, Serengeti',
    bestFor: 'Active travellers who want summit and safari in one trip',
    text: 'Reach Africa\'s highest peak with a private mountain crew, then recover with classic Northern Circuit game drives.',
    highlights: ['Machame or Lemosho route', 'Professional mountain crew', 'Post-climb safari extension'],
  },
  {
    image: SAFARI_PAGE_IMAGES.southern,
    title: 'Southern Wilderness Safari',
    duration: '5-7 days',
    tag: 'Remote parks',
    route: 'Mikumi, Nyerere, Ruaha',
    bestFor: 'Travellers who prefer fewer crowds and wild landscapes',
    text: 'A quieter safari through Tanzania\'s southern parks, with river safaris, walking options, and excellent predator country.',
    highlights: ['Boat safari options', 'Remote lodge stays', 'Big cat and elephant territory'],
  },
  {
    image: DESTINATION_IMAGES.zanzibar,
    title: 'Safari & Zanzibar Finale',
    duration: '8-12 days',
    tag: 'Most popular',
    route: 'Northern Circuit or Southern Circuit, Zanzibar',
    bestFor: 'Couples, families, and honeymoon-style journeys',
    text: 'Pair mainland wildlife with Stone Town heritage, spice farms, and Indian Ocean beach time for a complete Tanzania holiday.',
    highlights: ['Safari plus beach', 'Stone Town culture', 'Beach lodge relaxation'],
  },
  {
    image: SAFARI_PAGE_IMAGES.gameDrive,
    title: 'Short Tanzania Safari',
    duration: '3-4 days',
    tag: 'Quick escape',
    route: 'Tarangire, Lake Manyara, Ngorongoro',
    bestFor: 'Visitors with limited time in Northern Tanzania',
    text: 'A compact safari that still delivers strong wildlife viewing, scenic drives, and the world-famous Ngorongoro Crater.',
    highlights: ['Weekend friendly', 'Flexible lodge level', 'Easy Arusha start and finish'],
  },
  {
    image: SAFARI_PAGE_IMAGES.cultural,
    title: 'Culture, Safari & Day Trips',
    duration: '4-6 days',
    tag: 'Local connection',
    route: 'Arusha, Maasai or Chagga villages, nearby parks',
    bestFor: 'Guests who want wildlife, people, food, and everyday Tanzania',
    text: 'Blend cultural visits, waterfalls, coffee tours, and light safari days into a relaxed itinerary around Arusha and Moshi.',
    highlights: ['Village visits', 'Waterfalls and coffee tours', 'Add-on safari days'],
  },
  {
    image: ZANZIBAR_PAGE_IMAGES.galleryStonetown,
    title: 'Tanzania Historical & Heritage Journey',
    duration: '7-9 days',
    tag: 'History & heritage',
    route: 'Dar es Salaam, Bagamoyo, Kilwa, Stone Town',
    bestFor: 'History lovers, educational groups, and cultural travellers',
    text: 'Trace Tanzania\'s Swahili, trading, colonial, and independence history through coastal towns, archaeological sites, museums, and guided heritage walks.',
    highlights: ['Bagamoyo heritage sites', 'Kilwa Kisiwani ruins', 'Stone Town guided history walk'],
  },
];

const SERVICE_CATEGORIES = [
  {
    title: 'Private Safaris',
    text: 'Custom game-drive itineraries with private vehicles, licensed guides, and lodges matched to your budget.',
  },
  {
    title: 'Mountain Trekking',
    text: 'Guided Kilimanjaro and Mount Meru climbs with route planning, mountain crews, meals, and safety support.',
  },
  {
    title: 'Zanzibar Holidays',
    text: 'Stone Town stays, beach lodges, spice tours, snorkeling, dhow sails, and safari-and-sea combinations.',
  },
  {
    title: 'Day Trips',
    text: 'Short escapes to national parks, waterfalls, hot springs, coffee farms, and cultural sites around Arusha and Moshi.',
  },
  {
    title: 'Cultural Tours',
    text: 'Respectful visits with Maasai, Chagga, Hadzabe, Datoga, and local communities, guided by people who know the area.',
  },
  {
    title: 'Custom Combinations',
    text: 'Safari, trekking, beach, culture, and city logistics combined into one smooth Tanzania travel plan.',
  },
];

const INCLUDED_ITEMS = [
  'Trip planning based on your dates, budget, comfort level, and travel style',
  'Private or small-group transport with experienced local guides',
  'Accommodation suggestions from camping to premium lodges and beach resorts',
  'Park fees, activity timing, route logistics, transfers, and domestic flight advice',
  'Clear itinerary notes so clients understand what each package includes',
  'On-ground support from arrival until the final transfer',
];

const PLANNING_STEPS = [
  {
    step: '01',
    title: 'Choose a package style',
    text: 'Start with safari, mountain, island, day trip, culture, or a full Tanzania combination.',
  },
  {
    step: '02',
    title: 'Tell us your travel details',
    text: 'Share your dates, group size, budget, accommodation preference, and must-see places.',
  },
  {
    step: '03',
    title: 'Receive a tailored plan',
    text: 'We prepare a day-by-day itinerary with route, activities, lodges, and transparent inclusions.',
  },
  {
    step: '04',
    title: 'Confirm and travel',
    text: 'Once the itinerary feels right, we handle bookings and support you on the ground.',
  },
];

function Packages() {
  const [heroSlideIndex, setHeroSlideIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setHeroSlideIndex((current) => (current + 1) % PACKAGES_HERO_SLIDES.length);
    }, HERO_SLIDE_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    preloadImage(PACKAGES_HERO_SLIDES[0]?.src);
  }, []);

  useEffect(() => {
    const nextIndex = (heroSlideIndex + 1) % PACKAGES_HERO_SLIDES.length;
    preloadImage(PACKAGES_HERO_SLIDES[nextIndex]?.src);
  }, [heroSlideIndex]);

  const heroSlidesToLoad = getHeroSlidesToLoad(
    heroSlideIndex,
    PACKAGES_HERO_SLIDES.length,
  );

  const scrollToPackages = () => {
    document.getElementById('packages-list')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="packages-page">
      <section className="packages-hero" aria-labelledby="packages-hero-heading">
        <div className="packages-hero__media" aria-hidden="true">
          {PACKAGES_HERO_SLIDES.map((slide, index) => {
            if (!heroSlidesToLoad.has(index)) return null;

            const isActive = index === heroSlideIndex;
            return (
              <SafeImage
                key={slide.id}
                className={`packages-hero__image${
                  isActive ? ' packages-hero__image--active' : ''
                }`}
                src={slide.src}
                alt=""
                loading={index === 0 ? 'eager' : 'lazy'}
                fetchPriority={isActive ? 'high' : 'auto'}
                decoding={isActive ? 'sync' : 'async'}
              />
            );
          })}
          <div className="packages-hero__overlay" />
        </div>
        <div className="page-container packages-hero__container">
          <div className="packages-hero__content">
            <nav className="packages-hero__breadcrumb" aria-label="Breadcrumb">
              <a href="/">Home</a>
              <span className="packages-hero__breadcrumb-sep" aria-hidden="true">/</span>
              <span>Packages</span>
            </nav>
            <p className="packages-hero__eyebrow">Tanzania travel packages</p>
            <h1 id="packages-hero-heading">
              Safari, trekking, culture, and beach packages built around you.
            </h1>
            <p className="packages-hero__lead">
              Explore ready-made starting points for Mori Tours clients, then let us
              customize every date, lodge, activity, and route into your perfect Tanzania journey.
            </p>
            <div className="packages-hero__actions">
              <button
                className="packages-btn packages-btn--primary"
                type="button"
                onClick={scrollToPackages}
              >
                Explore packages
              </button>
              <a className="packages-btn packages-btn--outline" href="#contact">
                Request custom quote
              </a>
            </div>
          </div>
        </div>
        <div className="packages-hero__stats">
          {PACKAGE_STATS.map((stat) => (
            <div key={stat.label} className="packages-hero__stat">
              <span className="packages-hero__stat-value">{stat.value}</span>
              <span className="packages-hero__stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section
        id="packages-list"
        className="packages-section packages-list"
        aria-labelledby="packages-list-heading"
      >
        <div className="page-container">
          <Reveal className="packages-section__header">
            <p className="packages-section__eyebrow">Featured packages</p>
            <h2 id="packages-list-heading">Popular ways to experience Tanzania</h2>
            <p className="packages-section__intro">
              These packages help clients quickly understand what Mori Tours can arrange.
              Every option is flexible, so durations, lodges, routes, and activities can change.
            </p>
          </Reveal>
          <ul className="packages-list__grid">
            {FEATURED_PACKAGES.map((pkg, index) => (
              <Reveal
                key={pkg.title}
                as="li"
                className="packages-card"
                delay={index * 80}
              >
                <div className="packages-card__image">
                  <SafeImage src={pkg.image} alt={pkg.title} />
                  <span className="packages-card__duration">{pkg.duration}</span>
                </div>
                <div className="packages-card__body">
                  <span className="packages-card__tag">{pkg.tag}</span>
                  <h3>{pkg.title}</h3>
                  <p>{pkg.text}</p>
                  <dl className="packages-card__meta">
                    <div>
                      <dt>Route</dt>
                      <dd>{pkg.route}</dd>
                    </div>
                    <div>
                      <dt>Best for</dt>
                      <dd>{pkg.bestFor}</dd>
                    </div>
                  </dl>
                  <ul className="packages-card__highlights">
                    {pkg.highlights.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <a className="packages-card__cta" href="#contact">
                    Enquire about this package &rarr;
                  </a>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        className="packages-section packages-services"
        aria-labelledby="packages-services-heading"
      >
        <div className="page-container">
          <Reveal className="packages-section__header packages-section__header--light">
            <p className="packages-section__eyebrow packages-section__eyebrow--light">
              Services we arrange
            </p>
            <h2 id="packages-services-heading">More than fixed itineraries</h2>
            <p className="packages-section__intro packages-section__intro--light">
              Clients can book one focused service or combine several into a complete
              Tanzania journey with one planning team.
            </p>
          </Reveal>
          <ul className="packages-services__grid">
            {SERVICE_CATEGORIES.map((service, index) => (
              <Reveal
                key={service.title}
                as="li"
                className="packages-services__card"
                delay={index * 70}
                variant="scale"
              >
                <span className="packages-services__number">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <h3>{service.title}</h3>
                <p>{service.text}</p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        className="packages-section packages-included"
        aria-labelledby="packages-included-heading"
      >
        <div className="page-container">
          <div className="packages-included__inner">
            <Reveal className="packages-included__copy">
              <p className="packages-section__eyebrow">What clients can expect</p>
              <h2 id="packages-included-heading">Clear planning from first idea to final transfer</h2>
              <p>
                A good package should help clients understand what is possible, what is
                included, and how the trip will flow. We keep the planning practical,
                transparent, and easy to adjust.
              </p>
              <ul className="packages-included__list">
                {INCLUDED_ITEMS.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </Reveal>
            <Reveal className="packages-included__image-wrap" variant="right" delay={200}>
              <SafeImage
                className="packages-included__image"
                src={SAFARI_PAGE_IMAGES.overview}
                alt="Lions resting in Tanzania savanna"
              />
              <div className="packages-included__note">
                <strong>Flexible by design</strong>
                <span>
                  Each package is a starting point. We adjust the route until it fits
                  your time, comfort level, interests, and budget.
                </span>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section
        className="packages-section packages-steps"
        aria-labelledby="packages-steps-heading"
      >
        <div className="page-container">
          <Reveal className="packages-section__header">
            <p className="packages-section__eyebrow">How booking works</p>
            <h2 id="packages-steps-heading">Simple steps to a custom itinerary</h2>
          </Reveal>
          <ol className="packages-steps__list">
            {PLANNING_STEPS.map((item, index) => (
              <Reveal
                key={item.step}
                as="li"
                className="packages-steps__item"
                delay={index * 90}
              >
                <span className="packages-steps__number">{item.step}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="packages-cta" aria-labelledby="packages-cta-heading">
        <div className="page-container">
          <Reveal variant="scale">
            <div className="packages-cta__inner">
              <p className="packages-cta__eyebrow">Ready to start?</p>
              <h2 id="packages-cta-heading">Tell us the package style you like.</h2>
              <p>
                We will turn it into a personalized Tanzania itinerary with route,
                activities, accommodation, and clear next steps.
              </p>
              <a className="packages-btn packages-btn--primary packages-btn--lg" href="#contact">
                Plan my package
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

export default Packages;
